import React, { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import AddIcon from '@material-ui/icons/Add'
import VisibilityIcon from '@material-ui/icons/Visibility'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import axios from 'axios'
import SubmittalItemsTable from './SubmittalItemsTable'
import SubmittalProductPicker from './SubmittalProductPicker'
import SubmittalPreview from './SubmittalPreview'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(1)
  },
  metadata: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2)
  },
  notesField: { gridColumn: '1 / -1' },
  section: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3)
  }
}))

const STATUS_COLORS = {
  draft: 'default',
  ready: 'primary',
  submitted: 'primary',
  approved: 'default',
  revision_requested: 'secondary'
}

const SubmittalEditor = ({ submittalId }) => {
  const classes = useStyles()
  const history = useHistory()
  const [submittal, setSubmittal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [seedDialogOpen, setSeedDialogOpen] = useState(false)
  const [seedResult, setSeedResult] = useState(null)
  const [seeding, setSeeding] = useState(false)
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' })
  const [exporting, setExporting] = useState(false)
  const [editingMeta, setEditingMeta] = useState(false)
  const [metaForm, setMetaForm] = useState({})

  const fetchSubmittal = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/submittals/${submittalId}`)
      setSubmittal(data)
      setMetaForm({
        name: data.name || '',
        generalContractor: data.generalContractor || '',
        architect: data.architect || '',
        projectAddress: data.projectAddress || '',
        notes: data.notes || ''
      })
    } catch (err) {
      console.error('Failed to load submittal:', err)
    } finally {
      setLoading(false)
    }
  }, [submittalId])

  useEffect(() => { fetchSubmittal() }, [fetchSubmittal])

  const handleSaveMeta = async () => {
    try {
      const { data } = await axios.put(`/api/submittals/${submittalId}`, metaForm)
      setSubmittal(data)
      setEditingMeta(false)
      setSnack({ open: true, message: 'Saved', severity: 'success' })
    } catch (err) {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' })
    }
  }

  const handleItemsAdded = () => {
    fetchSubmittal()
    setPickerOpen(false)
    setSnack({ open: true, message: 'Items added', severity: 'success' })
  }

  const handleItemUpdate = async (itemId, updates) => {
    try {
      const { data } = await axios.put(`/api/submittals/${submittalId}/items/${itemId}`, updates)
      setSubmittal(data)
    } catch (err) {
      setSnack({ open: true, message: 'Failed to update item', severity: 'error' })
    }
  }

  const handleItemDelete = async (itemId) => {
    try {
      const { data } = await axios.delete(`/api/submittals/${submittalId}/items/${itemId}`)
      setSubmittal(data)
      setSnack({ open: true, message: 'Item removed', severity: 'success' })
    } catch (err) {
      setSnack({ open: true, message: 'Failed to remove item', severity: 'error' })
    }
  }

  const handleSeedFromProject = async () => {
    if (!submittal.projectId) {
      setSnack({ open: true, message: 'No project linked. Edit metadata to link a project first.', severity: 'warning' })
      return
    }
    setSeeding(true)
    setSeedResult(null)
    try {
      const { data } = await axios.post(`/api/submittals/${submittalId}/seed-from-project`, {
        projectId: submittal.projectId
      })
      setSeedResult(data)
      setSeedDialogOpen(true)
      fetchSubmittal()
    } catch (err) {
      setSnack({ open: true, message: 'Failed to seed from project', severity: 'error' })
    } finally {
      setSeeding(false)
    }
  }

  const handleExportPdf = () => {
    setExporting(true)
    axios.post(`/api/submittals/${submittalId}/export-pdf`, {}, { responseType: 'blob' })
      .then(res => {
        const blob = new Blob([res.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `Submittal_${(submittal.name || 'export').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        a.remove()
      })
      .catch(() => setSnack({ open: true, message: 'PDF export failed — check server logs', severity: 'error' }))
      .finally(() => setExporting(false))
  }

  if (loading) return <Box p={3}><Typography>Loading...</Typography></Box>
  if (!submittal) return <Box p={3}><Typography color="error">Submittal not found</Typography></Box>

  const sortedItems = [...(submittal.items || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton onClick={() => history.push('/submittals')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">{submittal.name}</Typography>
        <Chip
          label={submittal.status.replace(/_/g, ' ')}
          color={STATUS_COLORS[submittal.status] || 'default'}
          size="small"
        />
        <div className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<PlaylistAddIcon />}
            onClick={handleSeedFromProject}
            disabled={seeding || !submittal.projectId}
          >
            {seeding ? 'Seeding...' : 'Seed from Project'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={sortedItems.length === 0}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={sortedItems.length === 0 || exporting}
          >
            {exporting ? 'Generating...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <Paper className={classes.metadata}>
        {editingMeta ? (
          <>
            <TextField
              label="Project Name"
              value={metaForm.name}
              onChange={(e) => setMetaForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="General Contractor"
              value={metaForm.generalContractor}
              onChange={(e) => setMetaForm(prev => ({ ...prev, generalContractor: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Architect"
              value={metaForm.architect}
              onChange={(e) => setMetaForm(prev => ({ ...prev, architect: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Project Address"
              value={metaForm.projectAddress}
              onChange={(e) => setMetaForm(prev => ({ ...prev, projectAddress: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes"
              value={metaForm.notes}
              onChange={(e) => setMetaForm(prev => ({ ...prev, notes: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              className={classes.notesField}
            />
            <Box display="flex" gap={1}>
              <Button size="small" variant="contained" color="primary" onClick={handleSaveMeta}>Save</Button>
              <Button size="small" onClick={() => setEditingMeta(false)}>Cancel</Button>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography variant="caption" color="textSecondary">General Contractor</Typography>
              <Typography>{submittal.generalContractor || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Architect</Typography>
              <Typography>{submittal.architect || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Project</Typography>
              <Typography>{submittal.projectName || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Address</Typography>
              <Typography>{submittal.projectAddress || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Notes</Typography>
              <Typography>{submittal.notes || '—'}</Typography>
            </Box>
            <Box>
              <Button size="small" variant="outlined" onClick={() => setEditingMeta(true)}>Edit</Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Items */}
      <div className={classes.section}>
        <Typography variant="h6">Items ({sortedItems.length})</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setPickerOpen(true)}
        >
          Add Items
        </Button>
      </div>

      <SubmittalItemsTable
        items={sortedItems}
        onUpdateItem={handleItemUpdate}
        onDeleteItem={handleItemDelete}
      />

      {/* Product Picker Dialog */}
      <SubmittalProductPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        submittalId={submittalId}
        onItemsAdded={handleItemsAdded}
      />

      {/* Preview Dialog */}
      {previewOpen && (
        <SubmittalPreview
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          submittal={{ ...submittal, items: sortedItems }}
        />
      )}

      {/* Seed Results Dialog */}
      <Dialog open={seedDialogOpen} onClose={() => setSeedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Seed Results</DialogTitle>
        <DialogContent>
          {seedResult && (
            <>
              <Typography gutterBottom><strong>{seedResult.summary}</strong></Typography>
              {seedResult.matched.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Matched:</Typography>
                  {seedResult.matched.map((m, i) => (
                    <Typography key={i} variant="body2">
                      {m.description} &rarr; {m.product} (score: {m.score})
                    </Typography>
                  ))}
                </Box>
              )}
              {seedResult.unmatched.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Not Matched (add manually):</Typography>
                  {seedResult.unmatched.map((u, i) => (
                    <Typography key={i} variant="body2" color="textSecondary">
                      {u.description} ({u.quantity} {u.unit})
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedDialogOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(prev => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default SubmittalEditor
